import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { globalGuardMode } from './legFaults.ts';
import { legReachable } from './legLocations.ts';
import { legIsManual, legLamp, type LegLamp, type LegSkuArming } from './legModel.ts';

/**
 * One physical leg (SKU × upstream × account × bucket × channel × model)
 * with its JP / CN probe slices folded together (#2658). The legs read
 * returns one record per region slice (0081); the slices share the member
 * list, only the probe state differs.
 */

/**
 * Display state. `record` is a `tripped` row on a SKU where no mechanism is
 * armed: kept as evidence, it demotes nothing (same split LegRow's chip made).
 */
export type LegState = LegLamp | 'record';

/** Worse wins: 摘除 > 影子 > 失败 > 摘除记录 > 金身 > 未探测 > 正常; manual covers the whole leg. */
const SEVERITY: Readonly<Record<LegState, number>> = {
  ok: 0,
  unprobed: 1,
  immune: 2,
  record: 3,
  failing: 4,
  shadow: 5,
  tripped: 6,
  manual: 7,
};

export function legStateSeverity(state: LegState): number {
  return SEVERITY[state];
}

/** Red / red outline / yellow: what 「只看异常」 keeps. Not 未探测, 金身, manual. */
export function legStateIsAbnormal(state: LegState): boolean {
  return state === 'tripped' || state === 'record' || state === 'shadow' || state === 'failing';
}

export function sliceState(slice: TargetProbeLegRecord, arming: LegSkuArming, nowMs: number): LegState {
  const lamp = legLamp(slice, nowMs);
  if (lamp === 'manual') return 'manual';
  const guard = globalGuardMode(slice);
  if (guard === 'act') return 'tripped';
  if (guard === 'shadow' && (lamp === 'ok' || lamp === 'unprobed' || lamp === 'failing')) {
    return 'shadow';
  }
  return lamp === 'tripped' && !arming.armed ? 'record' : lamp;
}

export interface RegionSlice {
  readonly region: string;
  readonly slice: TargetProbeLegRecord;
  readonly state: LegState;
}

export interface PhysicalLeg {
  readonly key: string;
  /** First slice: identity, members, and the anchor for actions. */
  readonly head: TargetProbeLegRecord;
  /** `jp`, `cn`, then any other region. */
  readonly slices: readonly RegionSlice[];
  readonly state: LegState;
  /** At least one member can take traffic (gateway §1 ladder). */
  readonly reachable: boolean;
}

export function physicalLegKey(leg: TargetProbeLegRecord): string {
  return [
    leg.sku_id,
    leg.upstream_id,
    leg.account_id,
    leg.key_bucket_id,
    leg.upstream_channel_id,
    leg.physical_model,
  ].join('\u0000');
}

const REGION_ORDER = ['jp', 'cn'];

function regionRank(region: string): number {
  const index = REGION_ORDER.indexOf(region);
  return index < 0 ? REGION_ORDER.length : index;
}

export function worstState(states: readonly LegState[]): LegState {
  return states.reduce<LegState>((worst, state) => (SEVERITY[state] > SEVERITY[worst] ? state : worst), 'ok');
}

/** Folds region slices of one physical leg; keeps the first-seen order of legs. */
export function foldPhysicalLegs(
  slices: readonly TargetProbeLegRecord[],
  armingOf: (skuId: string) => LegSkuArming,
  nowMs: number,
): readonly PhysicalLeg[] {
  const byKey = new Map<string, TargetProbeLegRecord[]>();
  for (const slice of slices) {
    const key = physicalLegKey(slice);
    byKey.set(key, [...(byKey.get(key) ?? []), slice]);
  }
  const out: PhysicalLeg[] = [];
  for (const [key, group] of byKey) {
    const head = group[0];
    if (head === undefined) continue;
    const arming = armingOf(head.sku_id);
    const ordered = [...group].sort((left, right) =>
      regionRank(left.region) - regionRank(right.region) || left.region.localeCompare(right.region));
    const regions = ordered.map((slice) => ({ region: slice.region, slice, state: sliceState(slice, arming, nowMs) }));
    out.push({
      key,
      head,
      slices: regions,
      state: legIsManual(head) ? 'manual' : worstState(regions.map((entry) => entry.state)),
      reachable: legReachable(head),
    });
  }
  return out;
}

/** Newest `last_probe_at` across the slices. */
export function physicalLastProbeAt(leg: PhysicalLeg): string | null {
  let newest: string | null = null;
  for (const { slice } of leg.slices) {
    if (slice.last_probe_at === null) continue;
    if (newest === null || Date.parse(slice.last_probe_at) > Date.parse(newest)) newest = slice.last_probe_at;
  }
  return newest;
}

/** Regions where this leg is really out of the first pick: an armed trip. */
export function retiredRegions(leg: PhysicalLeg): readonly string[] {
  return leg.slices.filter((entry) => entry.state === 'tripped').map((entry) => entry.region);
}
